import { useEffect, useState } from "react";
import {
  Select,
  MenuItem,
  SelectChangeEvent,
  Paper,
  Checkbox,
  Button,
  Box,
  Typography,
} from "@mui/material";
import { NavBar } from "../../Components/NavBar";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  useApiGetStates,
  useApiGetMunicipalitiesByState,
  useApiGetDocumentListByMunicipality,
  useApiRenameDocument,
} from "../../API/documentationCatalogs.api";
import {
  EC2Municipality,
  States,
  EC2Document,
} from "../../API/types/documentationCatalogs.types";
import { useNotificationStore } from "../../Zustand/notification.store";
import SelectedDocumentsTable, {
  SelectedDocument,
} from "./sub/SelectedDocumentsTable";

const RenamePdfs = () => {
  const navigate = useNavigate();
  const showNotification = useNotificationStore((s) => s.show);

  /**
   * States
   */
  const [currentState, setCurrentState] = useState<States | null>(null);
  const [currentMunicipality, setCurrentMunicipality] =
    useState<EC2Municipality | null>(null);
  const [states, setStates] = useState<States[]>([]);
  const [municipalities, setMunicipalities] = useState<EC2Municipality[]>([]);
  const [documents, setDocuments] = useState<EC2Document[]>([]);
  const [selectedDocuments, setSelectedDocuments] = useState<
    SelectedDocument[]
  >([]);
  const [checkedDocuments, setCheckedDocuments] = useState<Set<number>>(
    new Set()
  );

  /**
   * API
   */
  const { call: callGetStates } = useApiGetStates({
    onCompleted: (res) => {
      setStates(res.data);
    },
    onError: (err) => {
      showNotification(err?.detail || "Failed to load states", "error");
      setStates([]);
    },
  });

  const { call: callGetMunicipalitiesByState } = useApiGetMunicipalitiesByState(
    {
      onCompleted: (res) => {
        setMunicipalities(res.data);
      },
      onError: (err) => {
        err?.status !== 404 &&
          showNotification(
            err?.detail || "Failed to load municipalities",
            "error"
          );
      },
    }
  );

  const { call: callGetDocumentsByMunicipality } =
    useApiGetDocumentListByMunicipality({
      onCompleted: (res) => {
        setDocuments(res.data.documents ? res.data.documents : []);
      },
      onError: (err) => {
        err?.status !== 404 &&
          showNotification(err?.detail || "Failed to load documents", "error");
        setDocuments([]);
      },
    });

  const { call: callRenameDocument, loading: renameLoading } =
    useApiRenameDocument({
      onCompleted: () => {
        showNotification("Document renamed successfully", "success");
      },
      onError: (err) => {
        showNotification(err?.detail || "Failed to rename document", "error");
      },
    });

  /**
   * Callbacks
   */
  const handleStateChange = (event: SelectChangeEvent<string>) => {
    const stateIdStr = event.target.value;
    setCurrentMunicipality(null);
    setDocuments([]);
    setCheckedDocuments(new Set());

    if (stateIdStr && stateIdStr !== "") {
      const stateId = parseInt(stateIdStr);
      const selectedState = states.find((s) => s.stateId === stateId) || null;
      setCurrentState(selectedState);
      if (selectedState) {
        callGetMunicipalitiesByState(selectedState.stateId).catch(() => {
          setMunicipalities([]);
        });
      }
    } else {
      setCurrentState(null);
    }
  };

  const handleMunicipalityChange = (event: SelectChangeEvent<string>) => {
    const municipalityIdStr = event.target.value;
    setCheckedDocuments(new Set());

    if (municipalityIdStr && municipalityIdStr !== "") {
      const municipalityId = parseInt(municipalityIdStr);
      const selected =
        municipalities.find((m) => m.municipalityId === municipalityId) || null;
      setCurrentMunicipality(selected);
    } else {
      setCurrentMunicipality(null);
    }
  };

  const handleDocumentCheck = (document: EC2Document, checked: boolean) => {
    if (checked) {
      setCheckedDocuments((prev) => new Set(prev).add(document.documentId));
      setSelectedDocuments((prev) => [
        ...prev,
        {
          document,
          newName: document.name,
          state: currentState,
          municipality: currentMunicipality,
        },
      ]);
    } else {
      setCheckedDocuments((prev) => {
        const newSet = new Set(prev);
        newSet.delete(document.documentId);
        return newSet;
      });
      setSelectedDocuments((prev) =>
        prev.filter((item) => item.document.documentId !== document.documentId)
      );
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      // Select all available documents
      const newCheckedSet = new Set(checkedDocuments);
      const newSelectedDocs = [...selectedDocuments];

      availableDocuments.forEach((document) => {
        if (!newCheckedSet.has(document.documentId)) {
          newCheckedSet.add(document.documentId);
          newSelectedDocs.push({
            document,
            newName: document.name,
            state: currentState,
            municipality: currentMunicipality,
          });
        }
      });

      setCheckedDocuments(newCheckedSet);
      setSelectedDocuments(newSelectedDocs);
    } else {
      // Deselect all available documents
      const newCheckedSet = new Set(checkedDocuments);
      const newSelectedDocs = selectedDocuments.filter((item) => {
        const isAvailable = availableDocuments.some(
          (doc) => doc.documentId === item.document.documentId
        );
        if (isAvailable) {
          newCheckedSet.delete(item.document.documentId);
          return false;
        }
        return true;
      });

      setCheckedDocuments(newCheckedSet);
      setSelectedDocuments(newSelectedDocs);
    }
  };

  const handleRemoveDocument = (documentId: number) => {
    setCheckedDocuments((prev) => {
      const newSet = new Set(prev);
      newSet.delete(documentId);
      return newSet;
    });
    setSelectedDocuments((prev) =>
      prev.filter((item) => item.document.documentId !== documentId)
    );
  };

  const handleNameChange = (documentId: number, newName: string) => {
    setSelectedDocuments((prev) =>
      prev.map((item) =>
        item.document.documentId === documentId ? { ...item, newName } : item
      )
    );
  };

  const handleSubmitRenames = async () => {
    if (selectedDocuments.length === 0) {
      showNotification("No documents selected for renaming", "warning");
      return;
    }

    // Validate all names are filled
    const invalidDocuments = selectedDocuments.filter(
      (item) => !item.newName.trim()
    );
    if (invalidDocuments.length > 0) {
      showNotification(
        "Please provide a new name for all selected documents",
        "error"
      );
      return;
    }

    // Rename all documents
    try {
      await Promise.all(
        selectedDocuments.map((item) =>
          callRenameDocument(item.document.documentId, item.newName.trim())
        )
      );

      // Clear selections and refresh documents
      setSelectedDocuments([]);
      setCheckedDocuments(new Set());
      if (currentMunicipality) {
        callGetDocumentsByMunicipality(currentMunicipality.municipalityId);
      }
      showNotification("All documents renamed successfully", "success");
    } catch (error) {
      showNotification("Some documents failed to rename", "error");
    }
  };

  /**
   * Effects
   */
  useEffect(() => {
    callGetStates().catch(() => {
      setStates([]);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (currentMunicipality?.municipalityId) {
      callGetDocumentsByMunicipality(currentMunicipality.municipalityId).catch(
        () => setDocuments([])
      );
    } else {
      setDocuments([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMunicipality?.municipalityId]);

  // Filter out documents that are already in the selected table
  const selectedDocumentIds = new Set(
    selectedDocuments.map((item) => item.document.documentId)
  );
  const availableDocuments = documents.filter(
    (doc) => doc.type === "pdf" && !selectedDocumentIds.has(doc.documentId)
  );

  // Calculate select all checkbox state
  const checkedAvailableCount = availableDocuments.filter((doc) =>
    checkedDocuments.has(doc.documentId)
  ).length;
  const isAllSelected =
    availableDocuments.length > 0 &&
    checkedAvailableCount === availableDocuments.length;
  const isIndeterminate =
    checkedAvailableCount > 0 &&
    checkedAvailableCount < availableDocuments.length;

  return (
    <>
      <NavBar />
      <div className="p-4">
        {/* Header and Controls Row */}
        <Box
          sx={{
            mb: 3,
            display: "flex",
            gap: 3,
            alignItems: "flex-start",
            position: "relative",
          }}
        >
          {/* Back Button - Positioned over both sections */}
          <Button
            variant="text"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/home")}
            size="small"
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              zIndex: 1,
            }}
          >
            Back
          </Button>

          {/* Left: Title and Description */}
          <Box sx={{ flex: "0 0 auto", minWidth: 250 }}>
            <Typography variant="h4" component="h1" sx={{ mb: 1 }}>
              Rename PDFs
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select documents and rename them in bulk
            </Typography>
            {/* Selection Section */}
            <Box sx={{ display: "flex", gap: 2, flexDirection: "column" }}>
              <Select
                value={currentState?.stateId?.toString() ?? ""}
                displayEmpty
                onChange={handleStateChange}
                size="small"
                sx={{ minWidth: 200 }}
              >
                <MenuItem value="" disabled>
                  Select a State
                </MenuItem>
                {states
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((state) => (
                    <MenuItem
                      key={state.stateId}
                      value={state.stateId.toString()}
                    >
                      {state.name}
                    </MenuItem>
                  ))}
              </Select>

              <Select
                value={currentMunicipality?.municipalityId?.toString() ?? ""}
                displayEmpty
                onChange={handleMunicipalityChange}
                disabled={!currentState}
                size="small"
                sx={{ minWidth: 200 }}
              >
                <MenuItem value="" disabled>
                  Select a Municipality
                </MenuItem>
                {municipalities
                  .sort((a, b) =>
                    a.municipalityName.localeCompare(b.municipalityName)
                  )
                  .map((municipality) => (
                    <MenuItem
                      key={municipality.municipalityId}
                      value={municipality.municipalityId.toString()}
                    >
                      {municipality.municipalityName}
                    </MenuItem>
                  ))}
              </Select>
            </Box>
          </Box>

          {/* Right: Available Documents List */}
          {currentMunicipality && availableDocuments.length > 0 && (
            <Box sx={{ flex: "1 1 auto", minWidth: 300 }}>
              <Typography variant="h6" gutterBottom>
                Available Documents
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 1,
                  ml: 1,
                }}
              >
                <Checkbox
                  checked={isAllSelected}
                  indeterminate={isIndeterminate}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  size="small"
                />
                <Typography variant="body2">Select All Documents</Typography>
              </Box>
              <Paper sx={{ p: 2, maxHeight: 300, overflow: "auto" }}>
                {availableDocuments.map((document) => (
                  <Box
                    key={document.documentId}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      py: 1,
                      borderBottom: "1px solid #e0e0e0",
                    }}
                  >
                    <Checkbox
                      checked={checkedDocuments.has(document.documentId)}
                      onChange={(e) =>
                        handleDocumentCheck(document, e.target.checked)
                      }
                      size="small"
                    />
                    <Typography sx={{ flex: 1, ml: 1 }} variant="body2">
                      {document.name}
                    </Typography>
                  </Box>
                ))}
              </Paper>
            </Box>
          )}
        </Box>

        {/* Selected Documents Table */}
        <SelectedDocumentsTable
          selectedDocuments={selectedDocuments}
          onNameChange={handleNameChange}
          onRemove={handleRemoveDocument}
          onSubmit={handleSubmitRenames}
          loading={renameLoading}
        />

        {currentMunicipality &&
          availableDocuments.length === 0 &&
          selectedDocuments.length === 0 && (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                No PDF documents available for this municipality
              </Typography>
            </Box>
          )}
      </div>
    </>
  );
};

export default RenamePdfs;
